import JSZip from 'jszip';
import { 
  addSection, 
  addItem, 
  deleteSection, 
  deleteItem, 
  getMenu 
} from '@/lib/firebase/menus';
import { 
  addImageMenuItem, 
  deleteImageMenuItem, 
  getImageMenuItems 
} from '@/lib/firebase/imageMenus';
import { uploadFile } from '@/lib/firebase/storage';
import { updateRestaurant } from '@/lib/firebase/firestore';
import type { ExportData } from './menuExport';
import type { Restaurant } from '@/types';

/**
 * Extract filename from Firebase Storage URL
 */
function extractFilenameFromFirebaseUrl(url: string): string | null {
  try {
    // Firebase Storage URLs contain the full path
    const urlParts = url.split('/o/')[1];
    if (!urlParts) return null;
    
    const pathWithParams = urlParts.split('?')[0];
    const decodedPath = decodeURIComponent(pathWithParams);
    const pathSegments = decodedPath.split('/');
    return pathSegments[pathSegments.length - 1];
  } catch (error) {
    console.warn('Error extracting filename from URL:', error);
    return null;
  }
}

export interface ImportResult {
  success: boolean;
  importedSections: number;
  importedItems: number;
  importedImageMenuItems: number;
  errors: string[];
}

export interface ImportProgress {
  stage: 'validating' | 'clearing_existing' | 'uploading_images' | 'importing_data' | 'complete';
  message: string;
  progress: number; // 0-100
  currentItem?: string;
}

export interface ImportPreview {
  restaurant: {
    name: string;
    description?: string;
  };
  digitalMenuSummary: {
    sectionsCount: number;
    itemsCount: number;
    sectionNames: string[];
  };
  imageMenuSummary: {
    imagesCount: number;
  };
  hasExistingData: {
    digitalMenu: boolean;
    imageMenu: boolean;
  };
}

/**
 * Validate ZIP file structure and parse menu data
 */
export async function validateImportFile(file: File): Promise<{
  isValid: boolean;
  data?: ExportData;
  preview?: ImportPreview;
  error?: string;
}> {
  try {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      return { isValid: false, error: 'Please upload a ZIP file' };
    }

    const zip = new JSZip();
    const contents = await zip.loadAsync(file);

    // Check for required menu-data.json
    const menuDataFile = contents.file('menu-data.json');
    if (!menuDataFile) {
      return { 
        isValid: false, 
        error: 'Invalid menu export file: missing menu-data.json' 
      };
    }

    // Parse and validate JSON
    const menuDataText = await menuDataFile.async('text');
    let data: ExportData;
    
    try {
      data = JSON.parse(menuDataText);
    } catch (error) {
      return { 
        isValid: false, 
        error: 'Invalid menu data: corrupted JSON file' 
      };
    }

    // Validate data structure
    if (!data.metadata || data.metadata.type !== 'digital-menu-export') {
      return { 
        isValid: false, 
        error: 'Invalid file: not a digital menu export' 
      };
    }

    if (!data.restaurant || !data.restaurant.name) {
      return { 
        isValid: false, 
        error: 'Invalid export: missing restaurant data' 
      };
    }

    // Create preview
    const preview: ImportPreview = {
      restaurant: {
        name: data.restaurant.name,
        description: data.restaurant.description
      },
      digitalMenuSummary: {
        sectionsCount: data.digitalMenu.sections.length,
        itemsCount: data.digitalMenu.items.length,
        sectionNames: data.digitalMenu.sections.map(s => s.name)
      },
      imageMenuSummary: {
        imagesCount: data.imageMenu.length
      },
      hasExistingData: {
        digitalMenu: false, // Will be set when checking existing data
        imageMenu: false    // Will be set when checking existing data
      }
    };

    return { isValid: true, data, preview };
  } catch (error) {
    console.error('Error validating import file:', error);
    return { 
      isValid: false, 
      error: 'Failed to read ZIP file. Please ensure it\'s a valid export file.' 
    };
  }
}

/**
 * Check if restaurant has existing menu data
 */
export async function checkExistingData(restaurantId: string): Promise<{
  hasDigitalMenu: boolean;
  hasImageMenu: boolean;
}> {
  try {
    const [menuData, imageMenuItems] = await Promise.all([
      getMenu(restaurantId),
      getImageMenuItems(restaurantId)
    ]);

    return {
      hasDigitalMenu: menuData ? (menuData.sections.length > 0 || menuData.items.length > 0) : false,
      hasImageMenu: imageMenuItems.length > 0
    };
  } catch (error) {
    console.error('Error checking existing data:', error);
    return { hasDigitalMenu: false, hasImageMenu: false };
  }
}

/**
 * Clear existing menu data for a restaurant
 */
async function clearExistingData(
  restaurantId: string,
  onProgress?: (progress: ImportProgress) => void
): Promise<void> {
  onProgress?.({
    stage: 'clearing_existing',
    message: 'Removing existing menu data...',
    progress: 0
  });

  try {
    // Get existing data
    const [menuData, imageMenuItems] = await Promise.all([
      getMenu(restaurantId),
      getImageMenuItems(restaurantId)
    ]);

    const totalOperations = (menuData?.items.length || 0) + 
                           (menuData?.sections.length || 0) + 
                           imageMenuItems.length;
    
    let completedOperations = 0;

    // Delete existing digital menu items
    if (menuData?.items) {
      for (const item of menuData.items) {
        await deleteItem(item.id);
        completedOperations++;
        onProgress?.({
          stage: 'clearing_existing',
          message: `Removing existing items (${completedOperations}/${totalOperations})...`,
          progress: (completedOperations / totalOperations) * 40,
          currentItem: item.name
        });
      }
    }

    // Delete existing digital menu sections
    if (menuData?.sections) {
      for (const section of menuData.sections) {
        await deleteSection(section.id);
        completedOperations++;
        onProgress?.({
          stage: 'clearing_existing',
          message: `Removing existing sections (${completedOperations}/${totalOperations})...`,
          progress: (completedOperations / totalOperations) * 40,
          currentItem: section.name
        });
      }
    }

    // Delete existing image menu items
    for (const imageItem of imageMenuItems) {
      await deleteImageMenuItem(imageItem.id, imageItem.imageUrl, imageItem.thumbnailUrl);
      completedOperations++;
      onProgress?.({
        stage: 'clearing_existing',
        message: `Removing existing images (${completedOperations}/${totalOperations})...`,
        progress: (completedOperations / totalOperations) * 40
      });
    }

  } catch (error) {
    console.error('Error clearing existing data:', error);
    throw new Error('Failed to clear existing menu data');
  }
}

/**
 * Upload image files from ZIP to Firebase Storage
 */
async function uploadImagesFromZip(
  zip: JSZip,
  restaurantId: string,
  onProgress?: (progress: ImportProgress) => void
): Promise<{ [oldPath: string]: string }> {
  const imageUrlMap: { [oldPath: string]: string } = {};
  
  // Find all image files in the ZIP
  const imageFiles: Array<{ path: string; file: JSZip.JSZipObject }> = [];
  
  zip.forEach((relativePath, file) => {
    if ((relativePath.startsWith('images/') || relativePath === 'restaurant-logo.webp') && !file.dir) {
      imageFiles.push({ path: relativePath, file });
    }
  });

  const totalImages = imageFiles.length;
  
  for (let i = 0; i < imageFiles.length; i++) {
    const { path, file } = imageFiles[i];
    
    onProgress?.({
      stage: 'uploading_images',
      message: `Uploading images (${i + 1}/${totalImages})...`,
      progress: 50 + (i / totalImages) * 30,
      currentItem: path
    });

    try {
      const blob = await file.async('blob');
      const fileName = path.split('/').pop() || 'unknown.webp';
      
      // Create File object from blob
      const imageFile = new File([blob], fileName, { type: 'image/webp' });
      
      let uploadPath: string;
      
      if (path === 'restaurant-logo.webp') {
        uploadPath = `restaurants/${restaurantId}/logo.webp`;
      } else if (path.startsWith('images/menu-items/')) {
        uploadPath = `restaurants/${restaurantId}/menu-items/${fileName}`;
      } else if (path.startsWith('images/image-menu/')) {
        // For image menu items, we'll handle these separately
        // since they need both full and thumbnail versions
        continue;
      } else {
        continue; // Skip unknown paths
      }
      
      const result = await uploadFile(imageFile, uploadPath);
      imageUrlMap[path] = result.downloadURL;
      
    } catch (error) {
      console.warn(`Failed to upload image ${path}:`, error);
      // Continue with other images
    }
  }
  
  return imageUrlMap;
}

/**
 * Import menu data from validated export file
 */
export async function importMenu(
  file: File,
  data: ExportData,
  restaurant: Restaurant,
  onProgress?: (progress: ImportProgress) => void
): Promise<ImportResult> {
  const result: ImportResult = {
    success: false,
    importedSections: 0,
    importedItems: 0,
    importedImageMenuItems: 0,
    errors: []
  };

  try {
    const zip = new JSZip();
    const contents = await zip.loadAsync(file);

    // Stage 1: Clear existing data
    await clearExistingData(restaurant.id, onProgress);

    // Stage 2: Upload images
    onProgress?.({
      stage: 'uploading_images',
      message: 'Uploading images...',
      progress: 50
    });

    const imageUrlMap = await uploadImagesFromZip(contents, restaurant.id, onProgress);

    // Stage 3: Import data
    onProgress?.({
      stage: 'importing_data',
      message: 'Updating restaurant details...',
      progress: 80
    });

    // First, update restaurant details with imported data
    try {
      const restaurantUpdates: any = {
        name: data.restaurant.name,
        description: data.restaurant.description,
        phone: data.restaurant.phone,
        address: data.restaurant.address,
        themeColor: data.restaurant.themeColor,
      };

      // Update logo URL if it was uploaded
      const logoPath = 'restaurant-logo.webp';
      if (imageUrlMap[logoPath]) {
        restaurantUpdates.logoUrl = imageUrlMap[logoPath];
      } else if (data.restaurant.logoUrl) {
        // Still try to preserve the original logoUrl if no new one was uploaded
        restaurantUpdates.logoUrl = data.restaurant.logoUrl;
      }

      await updateRestaurant(restaurant.id, restaurantUpdates);
    } catch (error) {
      console.error('Failed to update restaurant details:', error);
      result.errors.push('Failed to update restaurant details');
    }

    onProgress?.({
      stage: 'importing_data',
      message: 'Importing menu sections...',
      progress: 85
    });

    // Import sections first (ordered by order field)
    const sortedSections = [...data.digitalMenu.sections].sort((a, b) => a.order - b.order);
    const sectionIdMap: { [order: number]: string } = {};
    
    for (const sectionData of sortedSections) {
      try {
        const section = await addSection({
          restaurantId: restaurant.id,
          name: sectionData.name,
          description: sectionData.description || '',
          order: sectionData.order,
          isVisible: sectionData.isVisible ?? true,
          collapsed: sectionData.collapsed ?? false
        });
        
        sectionIdMap[sectionData.order] = section.id;
        result.importedSections++;
      } catch (error) {
        console.error(`Failed to import section: ${sectionData.name}`, error);
        result.errors.push(`Failed to import section: ${sectionData.name} - ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    onProgress?.({
      stage: 'importing_data',
      message: 'Importing menu items...',
      progress: 90
    });

    // Import menu items (with updated image URLs and section IDs)
    const sortedItems = [...data.digitalMenu.items].sort((a, b) => {
      if (a.sectionOrder !== b.sectionOrder) {
        return a.sectionOrder - b.sectionOrder;
      }
      return a.order - b.order;
    });

    for (const itemData of sortedItems) {
      try {
        const sectionId = sectionIdMap[itemData.sectionOrder];
        if (!sectionId) {
          result.errors.push(`Failed to find section for item: ${itemData.name}`);
          continue;
        }

        // Update image URL if it was uploaded
        let imageUrl = itemData.imageUrl;
        if (imageUrl && itemData.imageUrl) {
          // Extract filename from Firebase Storage URL properly
          const extractedFileName = extractFilenameFromFirebaseUrl(itemData.imageUrl);
          if (extractedFileName) {
            const imagePath = `images/menu-items/${extractedFileName}`;
            if (imageUrlMap[imagePath]) {
              imageUrl = imageUrlMap[imagePath];
            } else {
              // If image wasn't found in the zip or failed to upload, don't include imageUrl
              imageUrl = undefined;
            }
          } else {
            // If we can't extract the filename, don't include imageUrl
            imageUrl = undefined;
          }
        }

        // Build the item data, excluding undefined values
        const itemToAdd: any = {
          restaurantId: restaurant.id,
          sectionId,
          name: itemData.name,
          description: itemData.description || '',
          price: itemData.price,
          dietType: itemData.dietType,
          order: itemData.order,
          isAvailable: itemData.isAvailable ?? true,
          isPopular: itemData.isPopular ?? false,
          isNew: itemData.isNew ?? false,
          ingredients: itemData.ingredients || [],
          allergens: itemData.allergens || []
        };

        // Add updated image URL if it was uploaded
        if (imageUrl) {
          itemToAdd.imageUrl = imageUrl;
          // Also preserve imageMetadata if it exists
          if (itemData.imageMetadata) {
            itemToAdd.imageMetadata = itemData.imageMetadata;
          }
        }

        // Only add optional fields if they have values
        if (itemData.preparationTime !== undefined && itemData.preparationTime !== null) {
          itemToAdd.preparationTime = itemData.preparationTime;
        }
        if (itemData.calories !== undefined && itemData.calories !== null) {
          itemToAdd.calories = itemData.calories;
        }
        if (itemData.spiceLevel !== undefined && itemData.spiceLevel !== null) {
          itemToAdd.spiceLevel = itemData.spiceLevel;
        }
        if (itemData.isVegan !== undefined && itemData.isVegan !== null) {
          itemToAdd.isVegan = itemData.isVegan;
        }
        if (itemData.isJain !== undefined && itemData.isJain !== null) {
          itemToAdd.isJain = itemData.isJain;
        }
        if (itemData.originalPrice !== undefined && itemData.originalPrice !== null) {
          itemToAdd.originalPrice = itemData.originalPrice;
        }
        // Note: imageMetadata is handled above along with imageUrl to ensure consistency

        // Enhanced diet type fields
        if (itemData.enhancedAllergens !== undefined && itemData.enhancedAllergens !== null) {
          itemToAdd.enhancedAllergens = itemData.enhancedAllergens;
        }
        if (itemData.enhancedSpiceLevel !== undefined && itemData.enhancedSpiceLevel !== null) {
          itemToAdd.enhancedSpiceLevel = itemData.enhancedSpiceLevel;
        }
        if (itemData.cuisineTags !== undefined && itemData.cuisineTags !== null) {
          itemToAdd.cuisineTags = itemData.cuisineTags;
        }
        if (itemData.customTags !== undefined && itemData.customTags !== null) {
          itemToAdd.customTags = itemData.customTags;
        }
        if (itemData.isSignatureDish !== undefined && itemData.isSignatureDish !== null) {
          itemToAdd.isSignatureDish = itemData.isSignatureDish;
        }
        if (itemData.isChefSpecial !== undefined && itemData.isChefSpecial !== null) {
          itemToAdd.isChefSpecial = itemData.isChefSpecial;
        }

        await addItem(itemToAdd);
        
        result.importedItems++;
      } catch (error) {
        console.error(`Failed to import item: ${itemData.name}`, error);
        result.errors.push(`Failed to import item: ${itemData.name} - ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Import image menu items
    const sortedImageItems = [...data.imageMenu].sort((a, b) => a.order - b.order);
    
    for (const imageData of sortedImageItems) {
      try {
        // Extract the filename from the original imageUrl to match what was used in export
        const extractedFileName = extractFilenameFromFirebaseUrl(imageData.imageUrl);
        if (!extractedFileName) {
          result.errors.push(`Failed to extract filename from image URL: ${imageData.imageUrl}`);
          continue;
        }
        
        // Upload both full and thumbnail images
        const fullImagePath = `images/image-menu/full/${extractedFileName}`;
        const thumbnailPath = `images/image-menu/thumbnails/${extractedFileName}`;
        
        const fullImageFile = contents.file(fullImagePath);
        const thumbnailFile = contents.file(thumbnailPath);
        
        if (fullImageFile && thumbnailFile) {
          const fullBlob = await fullImageFile.async('blob');
          const thumbnailBlob = await thumbnailFile.async('blob');
          
          // Create file objects (use original fileName for the File object name)
          const fullFile = new File([fullBlob], imageData.fileName, { type: 'image/webp' });
          const thumbFile = new File([thumbnailBlob], imageData.fileName, { type: 'image/webp' });
          
          // Upload to Firebase Storage
          const fullUploadPath = `restaurants/${restaurant.id}/image-menu/${extractedFileName}`;
          const thumbUploadPath = `restaurants/${restaurant.id}/image-menu/thumbnails/${extractedFileName}`;
          
          const [fullResult, thumbResult] = await Promise.all([
            uploadFile(fullFile, fullUploadPath),
            uploadFile(thumbFile, thumbUploadPath)
          ]);
          
          // Add to Firestore
          await addImageMenuItem(restaurant.id, {
            imageUrl: fullResult.downloadURL,
            thumbnailUrl: thumbResult.downloadURL,
            fileName: imageData.fileName,
            fileSize: imageData.fileSize,
            dimensions: imageData.dimensions
          }, result.importedImageMenuItems);
          
          result.importedImageMenuItems++;
        } else {
          result.errors.push(`Image files not found in ZIP for: ${imageData.fileName} (looking for ${fullImagePath} and ${thumbnailPath})`);
        }
      } catch (error) {
        console.error(`Failed to import image menu item: ${imageData.fileName}`, error);
        result.errors.push(`Failed to import image menu item: ${imageData.fileName} - ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    onProgress?.({
      stage: 'complete',
      message: 'Import completed successfully!',
      progress: 100
    });

    result.success = true;
    return result;

  } catch (error) {
    console.error('Import failed:', error);
    result.errors.push(error instanceof Error ? error.message : 'Import failed');
    result.success = false;
    return result;
  }
}
